import * as React from 'react'
import {
    Alert,
    Box,
    Container,
    FormControlLabel,
    Grid2,
    MenuItem,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import EditIcon from '@mui/icons-material/Edit'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'

import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import Navbar from '../setup/Navbar'
import HeadingText from '../../BuiltInComponent/HeadingTest'
import ButtonNormal from '../../BuiltInComponent/ButtonNormal'
import Loading from '../../BuiltInComponent/Loading'

import {
    copyRecipeFromHead,
    createRecipeDetail,
    deleteItem,
    getCompleteRecipies,
    getRecipeData,
    getRecipeHeads,
    getRecipeIngredientOptions,
    getSingleItem,
    updateSingleItem
} from '../../features/AdminManagement/recipeSlice'

export default function Recipe() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const { recipeHeads, ingredientOptions, recipeDetails, cmpltRcp, addRecipeLoading } = useSelector((state) => state.recipe)

    const [itemHeadCode, setItemHeadCode] = React.useState(location.state?.itemHeadCode || '')
    const [itemHeadName, setItemHeadName] = React.useState('')

    const [form, setForm] = React.useState({
        RONO: 0,
        itemCode: '',
        quantity: '',
        editMode: false
    })

    const [copyEnabled, setCopyEnabled] = React.useState(false)
    const [copySourceHead, setCopySourceHead] = React.useState('')
    const [alertState, setAlertState] = React.useState({ type: '', message: '' })

    React.useEffect(() => {
        dispatch(getRecipeHeads())
        dispatch(getCompleteRecipies())
    }, [dispatch])

    React.useEffect(() => {
        if (itemHeadCode) {
            dispatch(getRecipeData(itemHeadCode)).then((res) => {
                const head = res?.payload?.pm?.[0]
                setItemHeadName(head?.itemDesc || '')
            })
            dispatch(getRecipeIngredientOptions(itemHeadCode))
        }
    }, [dispatch, itemHeadCode])

    const resetForm = () => {
        setForm({ RONO: 0, itemCode: '', quantity: '', editMode: false })
    }

    const showError = (message) => setAlertState({ type: 'error', message })
    const showSuccess = (message) => setAlertState({ type: 'success', message })

    const validate = () => {
        if (!itemHeadCode) return 'Please select recipe head item'
        if (!form.itemCode) return 'Please select ingredient item'
        if (!form.quantity || Number(form.quantity) <= 0) return 'Quantity must be greater than 0'
        return null
    }

    const onSaveDetail = async () => {
        setAlertState({ type: '', message: '' })

        const error = validate()
        if (error) {
            showError(error)
            return
        }

        const payload = {
            itemHeadCode,
            itemCode: Number(form.itemCode),
            quantity: Number(form.quantity),
            RONO: form.RONO
        }

        if (form.editMode) {
            const result = await dispatch(updateSingleItem(payload))
            if (result.type.endsWith('fulfilled')) {
                showSuccess('Recipe ingredient updated successfully')
                resetForm()
            } else {
                showError(result.payload?.error || result.payload || 'Failed to update ingredient')
            }
            return
        }

        const result = await dispatch(createRecipeDetail(payload))
        if (result.type.endsWith('fulfilled')) {
            showSuccess('Recipe ingredient added successfully')
            resetForm()
        } else {
            showError(result.payload?.error || result.payload || 'Failed to add ingredient')
        }
    }

    const onEditClick = async (RONO) => {
        const result = await dispatch(getSingleItem(RONO))
        const row = result?.payload?.[0]
        if (!row) return

        setForm({
            RONO: row.RONO,
            itemCode: row.itemCode,
            quantity: row.quantity,
            editMode: true
        })
    }

    const onDeleteClick = async (RONO) => {
        const result = await dispatch(deleteItem({ RONO, itemHeadCode }))
        if (result.type.endsWith('fulfilled')) {
            showSuccess('Ingredient removed from recipe')
            if (Number(form.RONO) === Number(RONO)) {
                resetForm()
            }
        } else {
            showError(result.payload?.error || result.payload || 'Failed to delete ingredient')
        }
    }

    const onCopyRecipe = async () => {
        if (!itemHeadCode || !copySourceHead) {
            showError('Please select target and source recipe heads')
            return
        }

        if (Number(itemHeadCode) === Number(copySourceHead)) {
            showError('Source and target recipe heads cannot be same')
            return
        }

        const result = await dispatch(copyRecipeFromHead({
            targetItemHeadCode: Number(itemHeadCode),
            sourceItemHeadCode: Number(copySourceHead)
        }))

        if (result.type.endsWith('fulfilled')) {
            showSuccess('Complete recipe copied successfully')
            resetForm()
        } else {
            showError(result.payload?.error || result.payload || 'Failed to copy recipe')
        }
    }

    return (
        <>
            <Navbar />
            <Container sx={{ mt: 2, mb: 4 }}>
                <Grid2 container spacing={2}>
                    <Grid2 size={12}>
                        <HeadingText text='Recipe Management' />
                    </Grid2>

                    <Grid2 size={12}>
                        <KeyboardBackspaceIcon
                            sx={{
                                fontSize: 30,
                                color: 'white',
                                cursor: 'pointer',
                                backgroundColor: '#1D4988',
                                padding: 1,
                                borderRadius: 50
                            }}
                            onClick={() => navigate('/Recipe')}
                        />
                    </Grid2>

                    {alertState.message && (
                        <Grid2 size={12}>
                            <Alert severity={alertState.type || 'info'}>{alertState.message}</Alert>
                        </Grid2>
                    )}

                    <Grid2 size={{ xs: 12, md: 5 }}>
                        <TextField
                            select
                            label='Recipe Head Item'
                            fullWidth
                            size='small'
                            value={itemHeadCode}
                            onChange={(e) => {
                                setItemHeadCode(e.target.value)
                                setItemHeadName('')
                                setAlertState({ type: '', message: '' })
                                resetForm()
                            }}
                        >
                            {recipeHeads.map((head) => (
                                <MenuItem key={head.itemCode} value={head.itemCode}>
                                    {head.itemCode} - {head.itemDesc}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 7 }}>
                        <TextField
                            label='Selected Head Name'
                            fullWidth
                            size='small'
                            value={itemHeadName}
                            disabled
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <Paper variant='outlined' sx={{ p: 2 }}>
                            <Typography sx={{ fontWeight: 700, mb: 2 }}>Recipe Detail</Typography>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        select
                                        label='Ingredient Item'
                                        fullWidth
                                        size='small'
                                        value={form.itemCode}
                                        onChange={(e) => setForm((prev) => ({ ...prev, itemCode: e.target.value }))}
                                        disabled={!itemHeadCode}
                                    >
                                        {ingredientOptions.map((item) => (
                                            <MenuItem key={item.itemCode} value={item.itemCode}>
                                                {item.itemCode} - {item.itemDesc}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <TextField
                                        label='Quantity'
                                        type='number'
                                        fullWidth
                                        size='small'
                                        value={form.quantity}
                                        onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))}
                                        disabled={!itemHeadCode}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <ButtonNormal onClick={onSaveDetail} text={form.editMode ? 'Update' : 'Add'} />
                                </Grid2>
                            </Grid2>
                        </Paper>
                    </Grid2>

                    <Grid2 size={12}>
                        <Paper variant='outlined' sx={{ p: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={copyEnabled}
                                        onChange={(e) => setCopyEnabled(e.target.checked)}
                                    />
                                }
                                label='Copy Complete Recipe from another head'
                            />

                            {copyEnabled && (
                                <Grid2 container spacing={2} sx={{ mt: 0.5 }}>
                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                        <TextField
                                            select
                                            label='Source Recipe Head'
                                            fullWidth
                                            size='small'
                                            value={copySourceHead}
                                            onChange={(e) => setCopySourceHead(e.target.value)}
                                        >
                                            {cmpltRcp.map((item) => (
                                                <MenuItem key={item.itemHeadCode} value={item.itemHeadCode}>
                                                    {item.itemInRecipe}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 4 }}>
                                        <ButtonNormal onClick={onCopyRecipe} text='Copy Recipe' />
                                    </Grid2>
                                </Grid2>
                            )}
                        </Paper>
                    </Grid2>

                    <Grid2 size={12}>
                        <Paper variant='outlined' sx={{ p: 2 }}>
                            <Typography sx={{ fontWeight: 700, mb: 1 }}>Current Recipe Items</Typography>
                            {addRecipeLoading ? (
                                <Loading />
                            ) : (
                                <TableContainer>
                                    <Table size='small'>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>RONO</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Ingredient</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }} align='right'>Quantity</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }} align='center'>Edit</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }} align='center'>Delete</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(recipeDetails || []).map((row) => (
                                                <TableRow key={row.RONO}>
                                                    <TableCell>{row.RONO}</TableCell>
                                                    <TableCell>{row.itemDesc}</TableCell>
                                                    <TableCell align='right'>{Number(row.quantity || 0).toFixed(3)}</TableCell>
                                                    <TableCell align='center'>
                                                        <EditIcon sx={{ cursor: 'pointer' }} onClick={() => onEditClick(row.RONO)} />
                                                    </TableCell>
                                                    <TableCell align='center'>
                                                        <DeleteForeverIcon sx={{ cursor: 'pointer' }} onClick={() => onDeleteClick(row.RONO)} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {(!recipeDetails || recipeDetails.length === 0) && (
                                                <TableRow>
                                                    <TableCell colSpan={5} align='center'>
                                                        {itemHeadCode ? 'No ingredient added yet' : 'Select recipe head to load details'}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    </Grid2>
                </Grid2>
            </Container>
        </>
    )
}
